/**
 * The room-corner layer: per corner, the chamfer corner handle (its drag
 * triangle and, when the corner is cut, the diagonal hit line with its
 * selection accent), then any corner-hosted segments on the chamfer face.
 *
 * The four corner triangles appear only while the whole room, a primary
 * wall, or a room corner is selected; selecting any of them shows all four.
 * The chamfer diagonal hit line and its hosted segments stay regardless, so
 * a chamfered corner remains directly selectable and its windows always render.
 */

import { CORNER_NAMES, FloorPlanRoom, Point, roomCornerLineFrame, worldOnLine } from '@kaiizen/kai-floor-plan';
import { chamferData, diagCursor, EditorMutableState, hasChamfer, isLineSegmentSelected } from '@kaiizen/kai-floor-plan-editor-core';
import { Fragment } from 'react';
import { ChamferCornerHandle } from './handles';
import { usePlanExport } from './planExportContext';
import { SegmentGlyph } from './SegmentGlyph';

export interface CornersLayerProps {
  st: EditorMutableState;
  s: FloorPlanRoom;
  sp: (p: Point) => Point;
  scale: number;
}

export function CornersLayer({ st, s, sp, scale }: CornersLayerProps): React.JSX.Element {
  const exporting = usePlanExport();
  const showCornerControls = st.selection.kind === 'room'
    || st.selection.kind === 'edge' || st.selection.kind === 'corner';
  return (
    <>
      {CORNER_NAMES.map(corner => {
        const chamfer = chamferData(st, { kind: 'corner', corner })!;
        const lineFrame = roomCornerLineFrame(s, corner);
        return (
          <Fragment key={corner}>
            <ChamferCornerHandle
              chamfer={chamfer}
              project={sp}
              handle={`room:${corner}`}
              selected={st.selection.kind === 'corner' && st.selection.corner === corner}
              cursor={diagCursor(chamfer.sx, chamfer.sy)}
              diagonalFrame={lineFrame}
              showTriangle={showCornerControls}
              showDiagonal={!exporting}
            />
            {hasChamfer(chamfer.ch) && lineFrame && (s[corner]?.segments ?? []).map((seg, csi) => (
              <SegmentGlyph
                key={csi}
                wfn={(ss: number, o: number) => worldOnLine(lineFrame, ss, o)}
                perp={lineFrame.perp}
                seg={seg}
                bodyHandle={`ls:rc:${corner}:${csi}`}
                startHandle={`ls-s:rc:${corner}:${csi}`}
                endHandle={`ls-e:rc:${corner}:${csi}`}
                delHandle={`ls-d:rc:${corner}:${csi}`}
                active={isLineSegmentSelected(st.selection, { k: 'rc', corner }, csi)}
                sp={sp}
                scale={scale}
              />
            ))}
          </Fragment>
        );
      })}
    </>
  );
}
